// ── Core domain types — shared by game client, realtime server, api, rewards. ──

export interface Vec2 {
  x: number;
  y: number;
}

export type RealmId = 'town' | 'wilds' | 'badlands';
export const PVP_REALMS: RealmId[] = ['badlands'];

export type ElementType =
  | 'beast' | 'flame' | 'tide' | 'verdant' | 'stone' | 'gale' | 'venom' | 'frost';

/** A monster blueprint (original Monster-Hunter-style beast — NOT Pokémon IP). */
export interface MonsterSpecies {
  id: string;
  name: string;
  element: ElementType;
  baseStats: { hp: number; atk: number; def: number; spd: number };
  /** species this evolves into, if any */
  evolvesToId?: string;
  evolveLevel?: number;
  spriteKey: string;
  catchRate: number; // 0..1
}

/** A captured/owned monster instance. */
export interface MonsterInstance {
  id: string;
  speciesId: string;
  nickname?: string;
  level: number;
  xp: number;
  currentHp: number;
  moveIds: string[];
  ownerId: string;
}

export interface Move {
  id: string;
  name: string;
  element: ElementType;
  power: number;
  accuracy: number; // 0..1
  pp: number;
}

export type ItemKind = 'capture' | 'heal' | 'tool' | 'material' | 'key';
export interface Item {
  id: string;
  name: string;
  kind: ItemKind;
  stackable: boolean;
  description: string;
}
export interface InventorySlot {
  itemId: string;
  qty: number;
}

export interface Player {
  id: string;
  /** Solana wallet (base58) — identity + hold-to-play gate */
  wallet: string;
  username: string;
  avatar: string; // PFP key (a character-skin / creature icon id)
  bio: string; // short player bio (profile page)
  realm: RealmId;
  pos: Vec2;
  gold: number; // off-chain currency
  xp: number; // account XP (earned on wins)
  level: number; // account level, derived from xp via playerLevel()
  streakDays?: number; // consecutive days played (0 = first day) — drives the earnings multiplier
  streakMultiplier?: number; // current gold/XP multiplier from the streak (1×→3×)
  party: MonsterInstance[]; // active party (max 6)
  createdAt: string;
}

export type Rarity = 'Epic' | 'Rare' | 'Uncommon' | 'Common';

/** A real CollectorCrypt card won as a reward. */
export interface CardReward {
  id: string;
  winnerWallet: string;
  cardName: string;
  rarity: Rarity;
  insuredValueUsd: number;
  imageUrl: string | null;
  nftAddress: string | null;
  txSignature: string | null;
  vrfProof: string | null;
  createdAt: string;
}
